import Loading from 'components/common/loading/Loading'
import { useState, KeyboardEvent, useEffect } from 'react'
import { WizardData } from 'types/wizard'

import ProgressBar from './ProgressBar'
import useFields from './useFields'
import useWizardForm from './useWizardForm'
import FieldRender from './WizardFieldRender'
import WizardFooter from './WizardFooter'
import WizardHeader from './WizardHeader'

import 'react-toastify/dist/ReactToastify.css'

interface WizardProps {
  isOpen: boolean
  wizardData: WizardData
  displayBar?: boolean
  isLoading: boolean
  handleClose: () => void
  setSubmit: (data: any) => void
  /** When true, wizard renders inline without overlay (for embedding in a page). */
  embedded?: boolean
  /** When true and embedded, hide the wizard header (use page header/stepper instead). */
  hideHeaderWhenEmbedded?: boolean
}

const Wizard = ({
  isOpen,
  wizardData,
  displayBar = false,
  handleClose,
  setSubmit,
  isLoading,
  embedded = false,
  hideHeaderWhenEmbedded = false,
}: WizardProps) => {
  const { title, subtitle, submitActionLabel, steps } = wizardData
  const { isStepValid, setIsStepValid } = useFields()
  const { formValues, updateFormValue } = useWizardForm()
  const [currentStep, setCurrentStep] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    if (embedded) return
    if (isOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isOpen, embedded])

  useEffect(() => {
    setIsTransitioning(true)
    setTimeout(() => {
      setIsTransitioning(false)
    }, 100)
  }, [currentStep])

  const handleNext = () => {
    if (isStepValid) {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1))
        setIsStepValid(false)
        setIsTransitioning(false)
      }, 200)
    }
  }

  const handlePrev = () => {
    setIsTransitioning(true)

    setTimeout(() => {
      setCurrentStep(prev => Math.max(prev - 1, 0))
      setIsTransitioning(false)
    }, 200)
  }

  const handleDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNext()
    }
  }

  if (!isOpen) return null

  const showHeader = !embedded || !hideHeaderWhenEmbedded
  const inner = (
    <div
      className={
        embedded
          ? 'relative bg-imovWhite rounded-xl border border-gray-200 shadow-sm w-full flex flex-col min-h-[400px]'
          : 'relative bg-imovWhite rounded-lg shadow-lg max-w-3xl w-full max-h-full overflow-y-auto mx-2 sm:mx-4 md:mx-8 h-[90vh] sm:h-[80vh] md:h-[700px] flex flex-col justify-center z-900'
      }
      onClick={embedded ? undefined : (e) => e.stopPropagation()}
    >
      {showHeader && (
        <WizardHeader title={title} subtitle={subtitle} onClose={handleClose} />
      )}
      {displayBar && (
        <ProgressBar
          steps={steps}
          currentStep={steps[currentStep].name}
          hideLabel={embedded && hideHeaderWhenEmbedded}
        />
      )}

      <div
        className={`p-4 sm:px-8 md:px-20 flex items-center justify-center w-full ${
          embedded
            ? 'flex-shrink-0 overflow-visible min-h-0'
            : 'flex-grow overflow-y-auto'
        }`}
        onKeyDown={handleDown}
      >
        {isLoading ? (
          <Loading className="text-imovPrimary" />
        ) : (
          <div
            className={`w-full relative transform transition-opacity duration-200 ease-in-out ${
              embedded ? '' : 'max-h-full'
            } ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
          >
            <FieldRender
              field={steps[currentStep]}
              formValues={formValues}
              updateFormValue={updateFormValue}
              setIsStepValid={setIsStepValid}
            />
          </div>
        )}
      </div>
      <WizardFooter
        handleNext={handleNext}
        handlePrev={handlePrev}
        handleSubmit={() => setSubmit(formValues)}
        submitLabel={submitActionLabel}
        isStepValid={isStepValid}
        step={currentStep}
        steps={steps}
        disabled={isLoading}
      />
    </div>
  )

  if (embedded) {
    return inner
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {inner}
    </div>
  )
}

export default Wizard
